// Recipe editor search box: "200 g flour 2 whisk" → suggestions with parsed quantities.
// Port of Swift RecipeExistingItemSearchBox (+ ➕ "Create ingredient" when nothing matches).
import React, { useMemo, useState } from 'react';
import { View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { parseItemQuery, quantityFor, toNumber, type ItemQuantity } from '../logic/quantity';
import { rankItems, INGREDIENT_FIELD_WEIGHTS as W } from '../logic/search';
import { resolveUnit, SYSTEM_UNITS } from '../logic/units';
import { formatQuantity, ingredientLine } from '../logic/format';
import { useAllIngredients, useAllUtensils, useLibrary, createBlank } from '../store/library';
import { useDiet } from '../hooks';
import { useSettings } from '../store/settings';
import { Field, ListItem, Button } from './ui';
import Icon from './Icon';
import { INGREDIENT_AISLES, UTENSIL_AISLES, type Id } from '../types';
import { spacing, usePalette } from '../theme';

export type Pick = { kind: 'ingredient' | 'utensil'; id: Id; quantity: ItemQuantity };

export default function ItemSearchBox({ excludeIngredients, excludeUtensils, onPick }: { excludeIngredients: Set<Id>; excludeUtensils: Set<Id>; onPick: (p: Pick) => void }) {
  const { t } = useTranslation();
  const p = usePalette();
  const [query, setQuery] = useState('');
  const ingredients = useAllIngredients();
  const diet = useDiet();
  const utensils = useAllUtensils();
  const customUnits = useLibrary(s => s.units);
  const unitSystem = useSettings(s => s.unitSystem);

  const parsed = useMemo(() => parseItemQuery(query, SYSTEM_UNITS[unitSystem], Object.values(customUnits)), [query, unitSystem, customUnits]);

  const suggestions = useMemo(() => {
    if (!parsed.searchText.trim()) return [];
    const fields = (x: { title: string; synonyms: string[]; aisle: string; explainer: string }) => [
      { text: x.title, weight: W.title },
      { text: x.synonyms.join(' '), weight: W.synonyms },
      { text: t(`aisles.${x.aisle}` as 'aisles.other'), weight: W.aisle },
      { text: x.explainer, weight: W.explainer },
    ];
    const ing = rankItems(parsed.searchText, Object.values(ingredients).filter(i => !excludeIngredients.has(i.id) && !diet.ingredientHidden(i)), fields, i => i.title, 5)
      .map(r => r.item)
      .sort((a, b) => INGREDIENT_AISLES.indexOf(a.aisle) - INGREDIENT_AISLES.indexOf(b.aisle));
    const ute = rankItems(parsed.searchText, Object.values(utensils).filter(u => !excludeUtensils.has(u.id)), fields, u => u.title, 5)
      .map(r => r.item)
      .sort((a, b) => UTENSIL_AISLES.indexOf(a.aisle) - UTENSIL_AISLES.indexOf(b.aisle));
    return [
      ...ing.map(i => ({ kind: 'ingredient' as const, id: i.id, title: i.title, quantity: quantityFor(parsed, [i.title, ...i.synonyms]) })),
      ...ute.map(u => ({ kind: 'utensil' as const, id: u.id, title: u.title, quantity: quantityFor(parsed, [u.title, ...u.synonyms]) })),
    ].slice(0, 8);
  }, [parsed, ingredients, utensils, excludeIngredients, excludeUtensils, diet, t]);

  const label = (s: (typeof suggestions)[number]) => {
    if (s.kind === 'utensil') return ingredientLine(String(s.quantity.amount.value), null, s.title, t('common.of'));
    const unit = resolveUnit(s.quantity.unit, customUnits);
    return ingredientLine(formatQuantity(toNumber(s.quantity.amount), unit), unit?.symbol ?? null, s.title, t('common.of'));
  };

  const pick = (s: Pick) => {
    onPick(s);
    setQuery('');
  };

  return (
    <View style={{ gap: spacing.s }}>
      <Field
        placeholder={t('recipe.searchItems')}
        value={query}
        onChangeText={setQuery}
        autoCorrect={false}
        autoCapitalize="none"
        accessibilityLabel={t('recipe.searchItems')}
      />
      {suggestions.map(s => (
        <ListItem
          key={`${s.kind}:${s.id}`}
          title={label(s)}
          icon={s.kind === 'ingredient' ? 'ingredient' : 'utensil'}
          right={<Icon name="plus" color={p.info} />}
          chevron={false}
          onPress={() => pick({ kind: s.kind, id: s.id, quantity: s.quantity })}
        />
      ))}
      {parsed.searchText.trim().length >= 2 && !suggestions.some(s => s.title.toLowerCase() === parsed.searchText.trim().toLowerCase()) ? (
        <Button
          variant="plain"
          icon="plus"
          label={t('recipe.createIngredient', { title: parsed.searchText.trim() })}
          onPress={() => {
            const title = parsed.searchText.trim();
            const id = createBlank('ingredients', title.charAt(0).toUpperCase() + title.slice(1));
            pick({ kind: 'ingredient', id, quantity: parsed.quantity });
          }}
        />
      ) : null}
    </View>
  );
}
